import React, { useEffect, useState } from 'react';
import { Download, RefreshCw, CheckCircle, AlertCircle, X, ArrowDownCircle } from 'lucide-react';

type UpdateStatus =
  | { status: 'checking' }
  | { status: 'available'; version: string }
  | { status: 'downloading'; percent: number; bytesPerSecond: number; transferred: number; total: number }
  | { status: 'ready'; version: string }
  | { status: 'up-to-date' }
  | { status: 'error'; message: string };

const formatBytes = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const progressGradient = 'linear-gradient(90deg, #fb923c 0%, #f97316 100%)';

const getProgressPercent = (update: Extract<UpdateStatus, { status: 'downloading' }>) => {
  const direct = Number(update.percent);
  const fromBytes = update.total > 0 ? (update.transferred / update.total) * 100 : 0;
  const raw = Number.isFinite(direct) && direct > 0 ? direct : fromBytes;
  const clamped = Math.max(0, Math.min(100, raw));
  return clamped > 0 && clamped < 1 ? 1 : clamped;
};

interface UpdateNotificationProps {
  onStartFullscreen?: (data: UpdateStatus) => void;
}

const UpdateNotification: React.FC<UpdateNotificationProps> = ({ onStartFullscreen }) => {
  const [update, setUpdate] = useState<UpdateStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!window.api?.onUpdateStatus) return;
    const cleanup = window.api.onUpdateStatus((data) => {
      // Ignore non-visible statuses and startup-handled events
      if (data.status === 'checking' || data.status === 'up-to-date') return;
      if ((data as any).startup) return; // handled by fullscreen UpdateScreen
      setDismissed(false);
      setUpdate(data as UpdateStatus);
    });
    return cleanup;
  }, []);

  if (!update || dismissed) return null;
  const progressPercent = update.status === 'downloading' ? getProgressPercent(update) : 0;

  return (
    <div className="fixed bottom-5 right-5 z-[9999] w-80 rounded-xl border border-border-color bg-bg-secondary shadow-2xl overflow-hidden animate-fade-in">
      {/* Accent top bar */}
      <div
        className="h-0.5 w-full"
        style={{ background: 'var(--accent-color)' }}
      />

      <div className="p-4">
        {update.status === 'available' && (
          <>
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <ArrowDownCircle className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--accent-color)' }} />
                <div>
                  <p className="text-sm font-semibold text-text-primary">Mise à jour disponible</p>
                  <p className="text-xs text-text-muted">Version {update.version}</p>
                </div>
              </div>
              <button
                onClick={() => setDismissed(true)}
                className="text-text-muted hover:text-text-primary transition-colors mt-0.5"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={() => {
                window.api?.downloadUpdate();
                if (onStartFullscreen && update) onStartFullscreen(update);
                setDismissed(true);
              }}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-orange-500 py-2 text-sm font-semibold text-white transition-colors hover:bg-orange-400"
            >
              <Download className="h-4 w-4" />
              Télécharger
            </button>
          </>
        )}

        {update.status === 'downloading' && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <RefreshCw className="h-5 w-5 animate-spin flex-shrink-0 text-orange-400" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary">Téléchargement en cours…</p>
                <p className="text-xs text-text-muted truncate">
                  {formatBytes(update.transferred)} / {formatBytes(update.total)}
                  {update.bytesPerSecond > 0 && ` · ${formatBytes(update.bytesPerSecond)}/s`}
                </p>
              </div>
              <span className="text-sm font-bold tabular-nums text-orange-400">
                {Math.round(progressPercent)}%
              </span>
            </div>
            <div className="w-full rounded-full h-2 bg-black/30 overflow-hidden ring-1 ring-white/5">
              <div
                className="h-full rounded-full transition-[width] duration-300 ease-out shadow-[0_0_10px_rgba(251,146,60,0.28)]"
                style={{ width: `${progressPercent}%`, background: progressGradient }}
              />
            </div>
          </>
        )}

        {update.status === 'ready' && (
          <>
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 flex-shrink-0 text-green-400" />
                <div>
                  <p className="text-sm font-semibold text-text-primary">Prêt à installer</p>
                  <p className="text-xs text-text-muted">Version {update.version} téléchargée</p>
                </div>
              </div>
              <button
                onClick={() => setDismissed(true)}
                className="text-text-muted hover:text-text-primary transition-colors mt-0.5"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={() => window.api?.installUpdate()}
              className="w-full flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold text-white bg-green-500 hover:bg-green-600 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Redémarrer et installer
            </button>
          </>
        )}

        {update.status === 'error' && (
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-error" />
              <div>
                <p className="text-sm font-semibold text-text-primary">Erreur de mise à jour</p>
                <p className="text-xs text-text-muted line-clamp-2">{update.message}</p>
              </div>
            </div>
            <button
              onClick={() => setDismissed(true)}
              className="text-text-muted hover:text-text-primary transition-colors mt-0.5 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateNotification;
